/**
 * useFeedCardTracking — web analog of the mobile useFeedImpressions hook.
 * Mobile gets impression/dwell tracking for free from FlatList's
 * onViewableItemsChanged; the web grid has no such primitive, so this hook
 * reimplements the same contract with IntersectionObserver, one instance per
 * card.
 *
 * USAGE — do not call this directly inside a .map() in the parent (that's a
 * Rules-of-Hooks violation once the list length changes). Wrap each card in
 * its own small component instead, e.g.:
 *
 *     const TrackedCouponCard = ({ item, index, feedId, feedSort, ...cardProps }) => {
 *       const { cardRef, markClicked } = useFeedCardTracking({
 *         couponId: item.id,
 *         businessId: item.business?.id ?? null,
 *         feedId,
 *         feedSort,
 *         feedPosition: index,
 *       });
 *       return (
 *         <div ref={cardRef}>
 *           <CustomerCouponCard
 *             {...cardProps}
 *             data={item}
 *             onPress={() => { markClicked(); cardProps.onPress(); }}
 *           />
 *         </div>
 *       );
 *     };
 *
 *     // in the parent's .map:
 *     {displayedCouponList.map((item, index) => (
 *       <TrackedCouponCard key={item.id} item={item} index={index} feedId="home_newest_coupons" feedSort="newest" ... />
 *     ))}
 *
 * WHAT COUNTS AS AN IMPRESSION: the card must be at least VIEWABILITY_THRESHOLD
 * visible for VIEWABILITY_MIN_MS continuous ms — the same "50% for 1s" MRC
 * viewability convention, so a fast scroll-through doesn't count as a real
 * impression. Tune the two constants below if that's not the bar you want.
 *
 * WHAT COUNTS AS A SCROLL-PAST: a card that DID register an impression, then
 * left the viewport (or unmounted — e.g. replaced during pagination, or the
 * page navigated away) without ever being clicked. Fires at most once per
 * card per mount.
 *
 * DWELL: accumulated across every continuous window the card was
 * >=VIEWABILITY_THRESHOLD visible (so scrolling a card in and out of view
 * repeatedly adds up, rather than only counting the last window).
 *
 * NOTE ON THE WRAPPING <div>: this hook needs a real, laid-out DOM node to
 * observe, so the wrapper can't use `display: contents`. If the card grid is
 * CSS Grid, a plain block-level wrapper div is normally invisible to layout
 * as long as the card component itself fills 100% width/height of it —
 * worth a visual check the first time this goes in, in case the grid was
 * relying on the card being a direct grid item.
 *
 * KNOWN TRADE-OFF: markClicked() fires unconditionally on press, including
 * the rare case where the press was actually swallowed upstream (e.g. the
 * card is in delete-mode, or the coupon turned out to be expired and the
 * handler just shows an alert instead of navigating). Left as-is to keep
 * this a self-contained addition — flag if that inaccuracy turns out to
 * matter for your dashboards and we can thread a "did it actually navigate"
 * signal back from the caller instead.
 */
import { useCallback, useEffect, useRef } from 'react';
import { trackImpression, trackScrollPast, trackClick } from './analytics.js'; 

const VIEWABILITY_THRESHOLD = 0.5; // fraction of the card that must be visible
const VIEWABILITY_MIN_MS = 1000;   // ...for at least this long, continuously

export function useFeedCardTracking({ couponId, businessId, feedId, feedSort, feedPosition }) {
  const cardRef = useRef(null);
  const enteredAtRef = useRef(null);        // ts the card most recently crossed into "viewable"
  const dwellAccumRef = useRef(0);          // total ms accumulated across all viewable windows
  const impressionFiredRef = useRef(false);
  const impressionTimerRef = useRef(null);
  const clickedRef = useRef(false);
  const scrollPastFiredRef = useRef(false); // scroll_past fires at most once per mount

  const flushDwellWindow = useCallback(() => {
    if (enteredAtRef.current !== null) {
      dwellAccumRef.current += Date.now() - enteredAtRef.current;
      enteredAtRef.current = null;
    }
  }, []);

  const maybeFireScrollPast = useCallback(() => {
    if (impressionFiredRef.current && !clickedRef.current && !scrollPastFiredRef.current) {
      scrollPastFiredRef.current = true;
      trackScrollPast({
        couponId,
        businessId,
        feedId,
        feedSort,
        feedPosition,
        dwellMs: dwellAccumRef.current,
      });
    }
  }, [couponId, businessId, feedId, feedSort, feedPosition]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        
        if (entry.isIntersecting && entry.intersectionRatio >= VIEWABILITY_THRESHOLD) {
          enteredAtRef.current = Date.now();
          if (!impressionFiredRef.current && !impressionTimerRef.current) {
            impressionTimerRef.current = setTimeout(() => {
              impressionTimerRef.current = null;
              impressionFiredRef.current = true;
              trackImpression({
                couponId,
                businessId,
                feedId,
                feedSort,
                feedPosition,
                dwellMs: VIEWABILITY_MIN_MS,
                eligible: true,
              });
            }, VIEWABILITY_MIN_MS);
          }
        } else {
          if (impressionTimerRef.current) {
            clearTimeout(impressionTimerRef.current);
            impressionTimerRef.current = null;
          }
          flushDwellWindow();
          maybeFireScrollPast();
        }
      },
      { threshold: [0, VIEWABILITY_THRESHOLD, 1] },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (impressionTimerRef.current) clearTimeout(impressionTimerRef.current);
      flushDwellWindow();
      maybeFireScrollPast();
    };
    // couponId/feedId identify the card; the rest (businessId/feedSort/
    // feedPosition) can't meaningfully change mid-mount for the same card,
    // so they're read fresh via the callbacks above rather than
    // re-triggering the observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponId, feedId, flushDwellWindow, maybeFireScrollPast]);

  const markClicked = useCallback(() => {
    clickedRef.current = true;
    flushDwellWindow();
    trackClick({
      couponId,
      businessId,
      feedId,
      feedSort,
      feedPosition,
      dwellMs: dwellAccumRef.current,
    });
  }, [couponId, businessId, feedId, feedSort, feedPosition, flushDwellWindow]);

  return { cardRef, markClicked };
}